import { Question } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";
import { TelegramHelp } from "./TelegramHelp";

/** `work.telegram.status | link | unlink` — bare JSON, no envelope (Docit Work 05 §8). */
interface TelegramStatus {
  configured: boolean;
  bot: string | null;
  linked: boolean;
  username: string | null;
  linkedAt: string | null;
  connectUrl: string | null;
  connectExpiresAt: string | null;
}

const KEY = ["work", "telegram", "status"] as const;

/**
 * Docit Work 05 §8 — connect MY Telegram chat. Questions waiting for me then arrive there as a
 * card: a tap on an option answers it, or a reply to the message does. Connecting is a one-time
 * code the server mints for the signed-in person; the bot binds the chat that presses Start, so
 * nobody can type in someone else's chat id. While a code is out the status is polled, so the
 * page flips to "Connected" by itself once the person presses Start in Telegram.
 */
export function Telegram() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ url: string; expiresAt: string | null } | null>(null);
  // The help window (2026-10-17): a button here, and open on arrival from the Mail area's "How to set up Telegram".
  const search = useSearch({ strict: false }) as { help?: string };
  const [helpOpen, setHelpOpen] = useState(search.help === "setup");

  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.research.post("work.telegram.status", {})) as TelegramStatus,
    refetchInterval: (query) =>
      pending && !(query.state.data as TelegramStatus | undefined)?.linked ? 3000 : false,
  });

  const link = useMutation({
    mutationFn: async () => (await api.research.post("work.telegram.link", {})) as TelegramStatus,
    onSuccess: (status) => {
      if (status.connectUrl) {
        setPending({ url: status.connectUrl, expiresAt: status.connectExpiresAt });
      }
    },
    onError: () => toast.error(t("Telegram could not be reached. Try again in a minute.")),
  });

  const unlink = useMutation({
    mutationFn: async () => (await api.research.post("work.telegram.unlink", {})) as TelegramStatus,
    onSuccess: (status) => {
      setPending(null);
      queryClient.setQueryData(KEY, status);
      toast.success(t("Telegram disconnected"));
    },
  });

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const help = (
    <>
      <div>
        <Button size="sm" variant="ghost" icon={Question} onClick={() => setHelpOpen(true)}>
          {t("How does this work?")}
        </Button>
      </div>
      <TelegramHelp
        open={helpOpen}
        onOpenChange={setHelpOpen}
        status={
          data
            ? {
                configured: data.configured,
                bot: data.bot,
                linked: data.linked,
                username: data.username,
              }
            : null
        }
      />
    </>
  );

  if (!data?.configured) {
    return (
      <div className="flex flex-col gap-3">
        <Text variant="small">
          {t(
            "Telegram is not set up on this server yet. An administrator needs to add the bot token.",
          )}
        </Text>
        {help}
      </div>
    );
  }

  if (data.linked) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 rounded-md border border-divider p-3">
          <div className="min-w-0">
            <p className="truncate text-body font-medium text-text">
              {data.username
                ? t("Connected as @{{username}}", { username: data.username })
                : t("Connected")}
            </p>
            {data.linkedAt ? (
              <p className="text-small text-text-tertiary">
                {t("Since {{when}}", { when: formatRelativeTime(data.linkedAt, i18n.language) })}
              </p>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate()}
          >
            {t("Disconnect")}
          </Button>
        </div>
        <Text variant="small">
          {t(
            "Questions waiting for you arrive in Telegram. Tap an option, or reply to the message, to answer. Send /inbox to the bot to see what is waiting.",
          )}
        </Text>
        {help}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Get the questions that are waiting for you in Telegram, and answer them with a tap or a reply.",
        )}
      </Text>
      {pending ? (
        <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
          <a
            href={pending.url}
            target="_blank"
            rel="noreferrer"
            className="text-body font-medium text-accent hover:underline"
          >
            {t("Open Telegram and press Start")}
          </a>
          <p className="text-small text-text-tertiary">
            {t("This link works once, for 15 minutes. This page updates when you are connected.")}
          </p>
        </div>
      ) : null}
      <div>
        <Button
          variant={pending ? "ghost" : "primary"}
          disabled={link.isPending}
          onClick={() => link.mutate()}
        >
          {pending ? t("Make a new link") : t("Connect Telegram")}
        </Button>
      </div>
      {help}
    </div>
  );
}
